import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { InformationRail as RailResponse } from "@shared/consumer-tvod";
import type { InformationCard } from "@shared/consumer-tvod";
import { portalApi, refreshSession } from "./api";
import { errorText } from "./errors";
import { useI18n, type Translate } from "./i18n";
import { useOnceVisible } from "./shelf";
import { usePortal } from "./state";
import { EmptyState, Link, Loading, Notice, PageTitle } from "./ui";
import { ChevronLeft, ChevronRight, DocumentIcon, PlayIcon } from "./icons";

/**
 * The portal's "Information" rail (2026-10-09,
 * `docs/design/portal-information.md`): short clips and PDFs the provider
 * shows its SIGNED-IN members, beside the catalogue. Never a title: no
 * padlock, no rental, no player engine - a clip plays in the browser's own
 * `<video>`, a PDF is drawn page by page by PDF.js.
 *
 *   - `InformationRail`: one carousel on the member's home; absent when the
 *     provider has published nothing (or the visitor is not signed in).
 *   - `/information`: every item, a grid. `/information/:id`: one item.
 *
 * The bytes come from session-gated, same-origin routes, so the cookie rides
 * on `<video src>` and on the PDF fetch. A PDF is never opened as a page of
 * its own: every `/api` response is CSP-sandboxed and Chrome refuses its PDF
 * viewer there - the Download button saves it instead.
 */

/* ── data ──────────────────────────────────────────────────────────────── */

type Loaded =
  | { state: "loading" }
  | { state: "error"; message: string }
  | { state: "ready"; items: InformationCard[] };

/**
 * One request shared by the side menu and the rail. The rail is the SAME for
 * every member of a portal (it is per portal, not per person), so a short
 * reuse cannot show one member another's content.
 */
const REUSE_MS = 60_000;
let shared: { at: number; promise: Promise<RailResponse> } | null = null;

function fetchInformation(): Promise<RailResponse> {
  if (shared && Date.now() - shared.at < REUSE_MS) return shared.promise;
  // Settled, never thrown: a failure is a missing rail, not a broken page.
  const promise = Promise.resolve().then(() => portalApi.information());
  shared = { at: Date.now(), promise };
  promise.catch(() => {
    if (shared?.promise === promise) shared = null;
  });
  return promise;
}

/** Tests start each case with nothing remembered. */
export function resetInformationCacheForTests() {
  shared = null;
}

/** The published items, for a signed-in member; none while signed out. */
export function useInformation(enabled = true): Loaded {
  const { t } = useI18n();
  const { me } = usePortal();
  const signedIn = !!me;
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });
  useEffect(() => {
    if (!signedIn || !enabled) return;
    let live = true;
    fetchInformation()
      .then((r) => live && setLoaded({ state: "ready", items: r.items }))
      .catch((err) => {
        if (live) setLoaded({ state: "error", message: errorText(err, t) });
      });
    return () => {
      live = false;
    };
  }, [signedIn, enabled, t]);
  return signedIn ? loaded : { state: "ready", items: [] };
}

/* ── labels ────────────────────────────────────────────────────────────── */

export function clipLength(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** "Video · 1:42" / "PDF · 12 sidor", for a badge or a line. */
export function kindLabel(item: InformationCard, t: Translate): string {
  if (item.kind === "video") {
    return item.durationSeconds
      ? t("info.kind.videoLength", { length: clipLength(item.durationSeconds) })
      : t("info.kind.video");
  }
  if (!item.pageCount) return t("info.kind.pdf");
  return item.pageCount === 1
    ? t("info.kind.pdfPage")
    : t("info.kind.pdfPages", { count: item.pageCount });
}

function sizeLabel(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 1
    ? `${mb.toFixed(1).replace(".0", "")} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

/* ── a card ────────────────────────────────────────────────────────────── */

function InfoArt({ item }: { item: InformationCard }) {
  const [failed, setFailed] = useState(false);
  if (item.posterUrl && !failed) {
    return (
      <img
        // A PDF's image is its first page (portrait): show its top, where
        // the title is, not the middle of the page.
        className={`poster poster--landscape info-art info-art--${item.kind}`}
        src={item.posterUrl}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span
      className={`poster poster--landscape poster--empty info-art info-art--${item.kind}`}
      aria-hidden="true"
    >
      {item.kind === "video" ? (
        <PlayIcon className="info-art__icon" />
      ) : (
        <DocumentIcon className="info-art__icon" />
      )}
    </span>
  );
}

export function InfoTile({ item }: { item: InformationCard }) {
  const { t } = useI18n();
  return (
    <li className="title-card info-card" data-info={item.id}>
      <Link to={`/information/${encodeURIComponent(item.id)}`} className="card">
        <span className="card__media">
          <InfoArt item={item} />
          <span className="card__badge info-card__badge">
            {item.kind === "video" ? (
              <PlayIcon className="card__badge-icon" />
            ) : (
              <DocumentIcon className="card__badge-icon" />
            )}
            <span>{kindLabel(item, t)}</span>
          </span>
        </span>
        <span className="title-card__name">{item.title}</span>
        {item.description && (
          <span className="title-card__meta info-card__desc">
            {item.description}
          </span>
        )}
      </Link>
    </li>
  );
}

/* ── the rail ──────────────────────────────────────────────────────────── */

/**
 * The Information carousel on a member's home. Asks for the items only when
 * it nears the viewport, and removes itself when there are none - a provider
 * that has not used the feature shows no empty heading.
 */
export function InformationRail() {
  const { t } = useI18n();
  const headId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);
  const visible = useOnceVisible(sectionRef);
  const loaded = useInformation(visible);

  if (loaded.state === "ready" && loaded.items.length === 0) return null;
  if (loaded.state === "error") return null;

  const scroll = (dir: 1 | -1) => {
    const track = trackRef.current;
    if (!track || typeof track.scrollBy !== "function") return;
    track.scrollBy({ left: dir * Math.max(200, track.clientWidth * 0.85) });
  };
  const label = t("info.title");
  const items = loaded.state === "ready" ? loaded.items : null;

  return (
    <section
      className="rail info-rail"
      ref={sectionRef}
      aria-labelledby={headId}
      data-rail="information"
    >
      <div className="rail__head">
        <h2 id={headId} className="rail__title">
          {label}
        </h2>
        <Link
          to="/information"
          className="rail__all"
          aria-label={t("store.rail.seeAllIn", { rail: label })}
        >
          {t("store.rail.seeAll")}
        </Link>
        {items && items.length > 1 && (
          <div className="rail__nav">
            <button
              type="button"
              className="rail__btn"
              onClick={() => scroll(-1)}
              aria-label={t("store.rail.prev", { rail: label })}
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              className="rail__btn"
              onClick={() => scroll(1)}
              aria-label={t("store.rail.next", { rail: label })}
            >
              <ChevronRight />
            </button>
          </div>
        )}
      </div>
      {items ? (
        <ul className="rail__track" ref={trackRef}>
          {items.map((item) => (
            <InfoTile key={item.id} item={item} />
          ))}
        </ul>
      ) : (
        <ul className="rail__track rail__track--loading" aria-hidden="true" />
      )}
    </section>
  );
}

/* ── /information ──────────────────────────────────────────────────────── */

export function InformationScreen() {
  const { t } = useI18n();
  const { context } = usePortal();
  const loaded = useInformation();
  return (
    <>
      <PageTitle>{t("info.title")}</PageTitle>
      {loaded.state === "loading" && <Loading />}
      {loaded.state === "error" && (
        <Notice kind="error">{loaded.message}</Notice>
      )}
      {loaded.state === "ready" && loaded.items.length === 0 && (
        <EmptyState icon={<DocumentIcon />} title={t("info.emptyTitle")}>
          <p>{t("info.empty", { portal: context.portal.name })}</p>
        </EmptyState>
      )}
      {loaded.state === "ready" && loaded.items.length > 0 && (
        <ul className="title-grid info-grid">
          {loaded.items.map((item) => (
            <InfoTile key={item.id} item={item} />
          ))}
        </ul>
      )}
    </>
  );
}

/* ── /information/:id ──────────────────────────────────────────────────── */

export function InformationItemScreen({ id }: { id: string }) {
  const { t } = useI18n();
  const loaded = useInformation();
  if (loaded.state === "loading") return <Loading />;
  if (loaded.state === "error") {
    return <Notice kind="error">{loaded.message}</Notice>;
  }
  const item = loaded.items.find((i) => i.id === id);
  if (!item) {
    // Unpublished since the link was made, or never this portal's.
    return (
      <EmptyState icon={<DocumentIcon />} title={t("info.gone")}>
        <p>
          <Link to="/information">{t("info.backToList")}</Link>
        </p>
      </EmptyState>
    );
  }
  return (
    <article className="info-item">
      <PageTitle>{item.title}</PageTitle>
      <p className="info-item__meta muted">
        {kindLabel(item, t)} · {sizeLabel(item.sizeBytes)}
      </p>
      {item.description && (
        <p className="info-item__desc">{item.description}</p>
      )}
      {item.kind === "video" ? (
        <ClipPlayer item={item} />
      ) : (
        <PdfViewer item={item} />
      )}
      <p>
        <a
          className="btn btn--quiet btn--inline"
          href={`${item.fileUrl}?download=1`}
          download
        >
          {t("info.download")}
        </a>
      </p>
    </article>
  );
}

/* ── a clip ────────────────────────────────────────────────────────────── */

/**
 * The browser's own player on the session-gated file. The access cookie
 * lives 15 minutes, and a `<video>` cannot refresh it: when a range read
 * fails, refresh the session ONCE and pick the clip up where it stopped.
 */
export function ClipPlayer({ item }: { item: InformationCard }) {
  const { t } = useI18n();
  const ref = useRef<HTMLVideoElement>(null);
  const retried = useRef(false);
  const [failed, setFailed] = useState(false);

  const onError = useCallback(async () => {
    const video = ref.current;
    if (!video) return;
    if (retried.current) {
      setFailed(true);
      return;
    }
    retried.current = true;
    const at = video.currentTime;
    const playing = !video.paused;
    if (!(await refreshSession())) {
      setFailed(true);
      return;
    }
    video.load();
    video.currentTime = at;
    if (playing) void video.play().catch(() => undefined);
  }, []);

  return (
    <div className="info-player">
      {failed ? (
        <Notice kind="error">{t("info.clipFailed")}</Notice>
      ) : (
        <video
          ref={ref}
          className="info-player__video"
          src={item.fileUrl}
          poster={item.posterUrl ?? undefined}
          controls
          playsInline
          preload="metadata"
          onError={() => void onError()}
        />
      )}
    </div>
  );
}

/* ── a PDF ─────────────────────────────────────────────────────────────── */

type PdfDoc = {
  numPages: number;
  getPage(n: number): Promise<PdfPage>;
  destroy(): Promise<void>;
};
type PdfPage = {
  getViewport(o: { scale: number }): { width: number; height: number };
  render(o: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }): { promise: Promise<void> };
};

/** Loads PDF.js lazily: ~1 MB that only this page needs. Replaceable in tests. */
export type PdfOpener = (data: ArrayBuffer) => Promise<PdfDoc>;

/**
 * One PDF.js worker for the page's life, handed over as a PORT: Vite's
 * `?worker` import works the same in dev and in the build (a `?url` import of
 * the `.mjs` does not - the dev server answers it with the worker's source).
 * A port-provided worker outlives `doc.destroy()`, so the next document
 * reuses it.
 */
let workerPort: Worker | null = null;

export const openPdf: PdfOpener = async (data) => {
  const [pdfjs, { default: PdfWorker }] = await Promise.all([
    import("pdfjs-dist"),
    import("pdfjs-dist/build/pdf.worker.min.mjs?worker"),
  ]);
  workerPort ??= new PdfWorker();
  pdfjs.GlobalWorkerOptions.workerPort = workerPort;
  // A PDF is the provider's file, not ours: keep PDF.js off `new Function`.
  return (await pdfjs.getDocument({
    data: new Uint8Array(data),
    isEvalSupported: false,
  }).promise) as unknown as PdfDoc;
};

let opener: PdfOpener = openPdf;
/** Tests swap PDF.js for a fake (jsdom has no canvas). */
export function setPdfOpenerForTests(next: PdfOpener | null) {
  opener = next ?? openPdf;
}

export function PdfViewer({ item }: { item: InformationCard }) {
  const { t } = useI18n();
  const [doc, setDoc] = useState<PdfDoc | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    let opened: PdfDoc | null = null;
    portalApi
      .informationBytes(item.fileUrl, ctrl.signal)
      .then((bytes) => opener(bytes))
      .then((d) => {
        opened = d;
        if (ctrl.signal.aborted) void d.destroy();
        else setDoc(d);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        // eslint-disable-next-line no-console
        console.warn("[information] pdf failed:", err);
        setFailed(true);
      });
    return () => {
      ctrl.abort();
      if (opened) void opened.destroy();
    };
  }, [item.fileUrl]);

  if (failed) return <Notice kind="error">{t("info.pdfFailed")}</Notice>;
  if (!doc) return <Loading />;
  return (
    <div className="pdf" role="document" aria-label={item.title}>
      {Array.from({ length: doc.numPages }, (_, i) => (
        <PdfPageView
          key={i}
          doc={doc}
          number={i + 1}
          label={t("info.page", { page: i + 1, count: doc.numPages })}
        />
      ))}
    </div>
  );
}

/**
 * One page, drawn when it nears the viewport, at the width it is shown at
 * times the screen's pixel ratio - sharp on a Retina screen, and a 60-page
 * guide does not draw 60 canvases before the first is read.
 */
function PdfPageView({
  doc,
  number,
  label,
}: {
  doc: PdfDoc;
  number: number;
  label: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const visible = useOnceVisible(box);
  const [ratio, setRatio] = useState<number | null>(null);

  useEffect(() => {
    if (!visible) return;
    let live = true;
    void (async () => {
      const page = await doc.getPage(number);
      const base = page.getViewport({ scale: 1 });
      const width = box.current?.clientWidth || 800;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: (width * dpr) / base.width });
      const c = canvas.current;
      const ctx = c?.getContext("2d");
      if (!live || !c || !ctx) return;
      c.width = Math.round(viewport.width);
      c.height = Math.round(viewport.height);
      setRatio(base.height / base.width);
      await page.render({ canvasContext: ctx, viewport }).promise;
    })().catch(() => undefined);
    return () => {
      live = false;
    };
  }, [visible, doc, number]);

  return (
    <div
      ref={box}
      className="pdf__page"
      // A4 until the page says otherwise, so the scrollbar does not jump.
      style={{ aspectRatio: `1 / ${ratio ?? Math.SQRT2}` }}
      aria-label={label}
      role="img"
    >
      <canvas ref={canvas} className="pdf__canvas" />
    </div>
  );
}

/** For the side menu: a link only when there is something to show. */
export function useHasInformation(): boolean {
  const loaded = useInformation();
  return loaded.state === "ready" && loaded.items.length > 0;
}
